import { useGame } from '@/contexts/GameContext';

interface GameWrapperProps {
  children: React.ReactNode;
  gameName: string;
  onQuit: () => void;
  showPause?: boolean;
}

export default function GameWrapper({ children, gameName, onQuit, showPause = true }: GameWrapperProps) {
  const { gameStatus, gameScore, gameTime, gameErrors, pauseGame, resumeGame, formatTime } = useGame();

  const handleQuit = () => {
    if (gameStatus === 'playing' || gameStatus === 'paused') {
      onQuit();
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      {/* Game Header */}
      <header className="bg-background-50 border-b border-background-200/70 px-4 md:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
        <button
          onClick={handleQuit}
          className="flex items-center gap-2 text-foreground-700 hover:text-foreground-950 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line text-lg"></i>
          <span className="hidden sm:inline">Retour aux jeux</span>
        </button>

        <h1 className="font-semibold text-foreground-950 text-sm md:text-base truncate mx-3">{gameName}</h1>

        <div className="flex items-center gap-3 md:gap-4">
          <div className="flex items-center gap-1.5 text-foreground-700 bg-background-100 rounded-lg px-2.5 py-1.5 text-xs md:text-sm">
            <i className="ri-timer-line"></i>
            <span className="font-mono font-medium">{formatTime(gameTime)}</span>
          </div>
          <div className="flex items-center gap-1.5 text-foreground-700 bg-background-100 rounded-lg px-2.5 py-1.5 text-xs md:text-sm">
            <i className="ri-star-line"></i>
            <span className="font-medium">{gameScore}</span>
          </div>
          {showPause && (
            <button
              onClick={gameStatus === 'paused' ? resumeGame : pauseGame}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 hover:bg-secondary-200 transition-colors cursor-pointer"
              aria-label={gameStatus === 'paused' ? 'Reprendre' : 'Pause'}
            >
              <i className={`${gameStatus === 'paused' ? 'ri-play-fill' : 'ri-pause-line'} text-foreground-700`}></i>
            </button>
          )}
        </div>
      </header>

      {/* Pause Overlay */}
      {gameStatus === 'paused' && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-background-50 rounded-3xl p-8 md:p-10 text-center shadow-2xl max-w-sm mx-4">
            <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-pause-line text-3xl text-foreground-950"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-950 mb-2">Pause</h2>
            <p className="text-foreground-600 mb-6 text-sm">Prends ton temps, la partie t&apos;attend !</p>
            <div className="flex flex-col gap-3">
              <button
                onClick={resumeGame}
                className="w-full py-3 bg-primary-500 text-background-50 rounded-xl font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-fill mr-2"></i> Reprendre
              </button>
              <button
                onClick={handleQuit}
                className="w-full py-3 bg-background-100 text-foreground-700 rounded-xl font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line mr-2"></i> Quitter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Game Content */}
      <main className="flex-1 flex flex-col">
        {children}
      </main>
    </div>
  );
}